import { Dialog } from '@base-ui/react/dialog';
import { clstx, cvaMerge } from '@payaca/utilities/cvaMerge';
import { ComponentPropsWithoutRef, forwardRef, PropsWithChildren } from 'react';
import { useOverlayPortalContainer } from '../OverlayPortalContainer/overlayPortalContainer';

const sidebarContainerClass = cvaMerge(
  'fixed flex transform flex-col border-gray-200 bg-white transition-all duration-300',
  {
    variants: {
      size: {
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- No utility for calc() or min() sizing
        sm: 'inset-y-0 h-full w-[min(280px,100%)]',
        // `md:inset-x-auto` undoes the mobile bottom-sheet's full-width inset
        // so the side-specific `md:start-0` / `md:end-0` from compoundVariants
        // can anchor the drawer to just one edge on desktop.
        standard:
          // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- iOS safe-area inset; No utility for calc() or min() sizing
          'inset-x-0 top-12 bottom-0 rounded-t-xl border-t pb-[env(safe-area-inset-bottom)] md:inset-x-auto md:inset-y-0 md:top-0 md:h-full md:rounded-t-none md:border-t-0 md:pb-0 md:w-[min(500px,100%)]',
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- iOS safe-area inset; No utility for calc() or min() sizing
        lg: 'inset-x-0 top-12 bottom-0 rounded-t-xl border-t pb-[env(safe-area-inset-bottom)] md:inset-x-auto md:inset-y-0 md:top-0 md:h-full md:rounded-t-none md:border-t-0 md:pb-0 md:w-[min(1000px,100%)]',
      },
      side: { left: '', right: '' },
      zIndexLevel: { 1: 'z-sidebar1', 2: 'z-sidebar2' },
    },
    compoundVariants: [
      { size: 'sm', side: 'left', class: 'start-0 border-e' },
      { size: 'sm', side: 'right', class: 'end-0 border-s' },
      { size: 'standard', side: 'left', class: 'md:start-0 md:border-e' },
      { size: 'standard', side: 'right', class: 'md:end-0 md:border-s' },
      { size: 'lg', side: 'left', class: 'md:start-0 md:border-e' },
      { size: 'lg', side: 'right', class: 'md:end-0 md:border-s' },
    ],
  }
);

const sidebarPanelClass = cvaMerge('transition-transform duration-300', {
  variants: {
    size: { sm: '', standard: '', lg: '' },
    side: { left: '', right: '' },
    behind: { true: '', false: '' },
  },
  compoundVariants: [
    {
      behind: true,
      size: 'standard',
      side: 'left',
      class: 'md:translate-x-36',
    },
    {
      behind: true,
      size: 'standard',
      side: 'right',
      class: 'md:-translate-x-36',
    },
    { behind: true, size: 'lg', side: 'left', class: 'md:translate-x-52' },
    { behind: true, size: 'lg', side: 'right', class: 'md:-translate-x-52' },
  ],
});

// Off-screen enter/exit position the drawer animates in from / out to. These
// MUST be written as complete static class strings — Tailwind's JIT only
// compiles classes it finds literally in the source, so interpolated variant
// names (e.g. `data-starting-style:${x}`) would generate no CSS and kill the
// slide. Standard / lg slide up from the bottom on mobile (bottom sheet) and in
// from the side on md+; sm stays side-attached everywhere.
const sidebarSlideClass = cvaMerge(
  'data-starting-style:opacity-0 data-ending-style:opacity-0',
  {
    variants: {
      size: { sm: '', standard: '', lg: '' },
      side: { left: '', right: '' },
    },
    compoundVariants: [
      {
        size: 'sm',
        side: 'left',
        class:
          'data-starting-style:-translate-x-full data-ending-style:-translate-x-full',
      },
      {
        size: 'sm',
        side: 'right',
        class:
          'data-starting-style:translate-x-full data-ending-style:translate-x-full',
      },
      {
        size: ['standard', 'lg'],
        side: 'left',
        class:
          'data-starting-style:translate-y-full data-starting-style:md:translate-y-0 data-starting-style:md:-translate-x-full data-ending-style:translate-y-full data-ending-style:md:translate-y-0 data-ending-style:md:-translate-x-full',
      },
      {
        size: ['standard', 'lg'],
        side: 'right',
        class:
          'data-starting-style:translate-y-full data-starting-style:md:translate-y-0 data-starting-style:md:translate-x-full data-ending-style:translate-y-full data-ending-style:md:translate-y-0 data-ending-style:md:translate-x-full',
      },
    ],
  }
);

export interface ISidebarCoreProps {
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
  disableBackdropClick?: boolean;
  panelProps?: ComponentPropsWithoutRef<'div'>;
  size?: 'sm' | 'standard' | 'lg';
  side?: 'left' | 'right';
  behind?: boolean;
  zIndexLevel?: 1 | 2;
  /**
   * Overlay behaviour, mapped 1:1 to Base UI `Dialog.Root`'s `modal`:
   * - `'trap-focus'` (default): focus is trapped, but background scroll stays
   *   unlocked and outside content stays interactive.
   * - `true`: background scroll is locked and outside content is inert.
   * - `false`: no focus trap.
   */
  modal?: boolean | 'trap-focus';
  /** Called after the open animation completes. */
  afterEnter?: () => void;
  /** Called after the close animation completes. */
  afterLeave?: () => void;
}

const SidebarCore = forwardRef<
  HTMLDivElement,
  PropsWithChildren<ISidebarCoreProps>
>((props, ref) => {
  const {
    className,
    children,
    isOpen,
    onClose,
    disableBackdropClick,
    panelProps,
    size = 'standard',
    side = 'right',
    behind = false,
    zIndexLevel = 1,
    modal = 'trap-focus',
    afterEnter,
    afterLeave,
  } = props;

  const container = useOverlayPortalContainer();

  return (
    <Dialog.Root
      open={isOpen}
      modal={modal}
      onOpenChange={(open, details) => {
        if (open) return;
        if (
          disableBackdropClick &&
          (details.reason === 'outside-press' ||
            details.reason === 'escape-key')
        ) {
          details.cancel();
          return;
        }
        onClose?.();
      }}
      onOpenChangeComplete={(open) => (open ? afterEnter?.() : afterLeave?.())}
    >
      <Dialog.Portal container={container ?? undefined}>
        <Dialog.Backdrop
          // Render even when nested, so a stacked sidebar dims the one beneath it.
          // Base UI hides nested backdrops by default; our z-index tokens
          // (z-sidebar-backdrop-1/2) are designed to layer them.
          forceRender
          className={clstx(
            'fixed inset-0 bg-gray-900/50',
            'transition-opacity duration-300 data-starting-style:opacity-0 data-ending-style:opacity-0',
            zIndexLevel === 1 && 'z-sidebar-backdrop-1',
            zIndexLevel === 2 && 'z-sidebar-backdrop-2'
          )}
        />
        <Dialog.Popup
          className={clstx(
            sidebarContainerClass({ size, side, zIndexLevel }),
            sidebarSlideClass({ size, side })
            // "translate-x-full"
          )}
        >
          <div
            ref={ref}
            className={clstx(
              className,
              sidebarPanelClass({ size, side, behind })
            )}
            {...panelProps}
          >
            {children}
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

export default SidebarCore;
